import React from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { usePageTracking } from '@byscott-io/metricsbyscott-web';
import {
  configureAuth,
  AuthProvider,
  ThemeProvider,
  MarketingRoute,
  useSpaLinkInterceptor,
} from '@byscott-io/corebyscott';

import LandingPage from './pages/LandingPage';
import HelpPage from './pages/HelpPage';
import LoginPage from './pages/LoginPage';
import SignupPage from './pages/SignupPage';
import DashboardPage from './pages/DashboardPage';

// Configure corebyscott for the affiliate marketing surface (#612/#614). A
// SEPARATE website served from the billing app on the affiliate host — its own
// branding, nav, footer, help corpus, and magic-link auth paths. Exported and
// called by application.js at MOUNT time (not import) so it only applies when
// the affiliate surface actually renders — never leaking onto the billing app.
export function configureAffiliateSurface() {
  configureAuth({
    appName: 'ByScott Affiliates',
    // Magic-link auth lives at these paths; the marketing header reads them.
    loginPath: '/login',
    registerPath: '/signup',
    dashboardPath: '/dashboard',
    documentationBasePath: '/help',
    marketing: {
      signInLabel: 'Sign In',
      getStartedLabel: 'Apply',
      dashboardLabel: 'Dashboard',
      navItems: [
        { path: '/', label: 'Overview' },
        { path: '/help', label: 'Help' },
        { path: '/login', label: 'Sign In' },
      ],
      footer: {
        companyName: 'IdeasByScott',
        companyDescription: 'Earn recurring commissions referring customers to the ByScott network.',
        productLinks: [
          { label: 'Apply', href: '/signup' },
          { label: 'Sign In', href: '/login' },
          { label: 'Help', href: '/help' },
        ],
        companyLinks: [
          { label: 'How referrals work', href: '/help/how-referrals-work' },
          { label: 'Getting paid', href: '/help/getting-paid' },
        ],
        legalLinks: [
          { label: 'Privacy Policy', href: 'https://byscott.io/privacy-policy' },
          { label: 'Terms of Service', href: 'https://byscott.io/terms' },
        ],
        copyright: `© ${new Date().getFullYear()} IdeasByScott. All rights reserved.`,
      },
    },
    logo: (
      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="w-7 h-7">
        {/* Megaphone — affiliate / referral */}
        <path d="M16.881 4.346A23.112 23.112 0 0 1 8.25 6H7.5a5.25 5.25 0 0 0-.88 10.427 21.593 21.593 0 0 0 1.378 3.94c.464 1.004 1.674 1.32 2.582.796l.657-.379c.88-.508 1.165-1.593.772-2.468a17.116 17.116 0 0 1-.628-1.607c1.918.258 3.76.75 5.5 1.446A21.727 21.727 0 0 0 18 11.25c0-2.413-.393-4.735-1.119-6.904ZM18.26 3.74a23.22 23.22 0 0 1 1.24 7.51 23.22 23.22 0 0 1-1.24 7.51c-.055.161-.111.322-.17.482a.75.75 0 1 0 1.409.516 24.555 24.555 0 0 0 1.415-6.43 2.992 2.992 0 0 0 .836-2.078c0-.806-.319-1.54-.836-2.078a24.65 24.65 0 0 0-1.415-6.43.75.75 0 1 0-1.409.516c.059.16.115.321.17.483Z" />
      </svg>
    ),
  });
}

function PageTracker() {
  const location = useLocation();
  usePageTracking(location.pathname);
  return null;
}

function SpaLinkInterceptor({ children }) {
  useSpaLinkInterceptor();
  return children;
}

// Affiliate-surface root (#612/#614). Marketing pages use corebyscott's
// MarketingLayout; /login + /signup are the magic-link forms; /dashboard is the
// authenticated affiliate area (gated client-side on the portal cookie session).
export default function AffiliateApp() {
  return (
    <ThemeProvider>
      <AuthProvider>
        <BrowserRouter>
          <PageTracker />
          <SpaLinkInterceptor>
            <Routes>
              <Route
                path="/"
                element={
                  <MarketingRoute>
                    <LandingPage />
                  </MarketingRoute>
                }
              />
              <Route
                path="/help/*"
                element={
                  <MarketingRoute>
                    <HelpPage />
                  </MarketingRoute>
                }
              />
              <Route path="/login" element={<LoginPage />} />
              <Route path="/signup" element={<SignupPage />} />
              <Route path="/dashboard" element={<DashboardPage />} />
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </SpaLinkInterceptor>
        </BrowserRouter>
      </AuthProvider>
    </ThemeProvider>
  );
}
